// CRM demo — primitives d'affichage Cosmos, autonomes (aucune dépendance au
// bundle Design System). Remplacent Badge / Avatar / ProgressBar / StatCard /
// timeline du kit DS par du markup Tailwind thématisé Cosmos.
import React from 'react';
import { Phone, Mail, StickyNote } from 'lucide-react';

// ─── Badge (pastille de statut) ───
// Tonalités sémantiques Cosmos ; classes statiques (pas de hex en dur).
const BADGE_TONES = {
  neutral: 'bg-slate-100 text-slate-600',
  info: 'bg-sky-100 text-sky-700',
  primary: 'bg-iris-100 text-iris-700',
  warning: 'bg-amber-100 text-amber-800',
  success: 'bg-emerald-100 text-emerald-700',
  danger: 'bg-rose-100 text-rose-700',
};
const BADGE_DOTS = {
  neutral: 'bg-slate-400',
  info: 'bg-sky-500',
  primary: 'bg-iris-500',
  warning: 'bg-amber-500',
  success: 'bg-emerald-500',
  danger: 'bg-rose-500',
};

export function Badge({ tone = 'neutral', dot = false, children, className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-2xs font-semibold ${BADGE_TONES[tone] || BADGE_TONES.neutral} ${className}`}
    >
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${BADGE_DOTS[tone] || BADGE_DOTS.neutral}`} aria-hidden="true" />
      )}
      {children}
    </span>
  );
}

// ─── Avatar (initiales) ───
const AVATAR_SIZES = {
  sm: 'w-7 h-7 text-2xs',
  md: 'w-9 h-9 text-xs',
  lg: 'w-14 h-14 text-xl',
};
export function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
export function Avatar({ name, size = 'md', className = '' }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full bg-iris-500 text-white font-bold flex-none ${AVATAR_SIZES[size] || AVATAR_SIZES.md} ${className}`}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

// ─── ProgressBar ───
// La largeur est une valeur runtime → `style` width % autorisé (cf. contraintes).
const PROGRESS_TONES = {
  primary: 'bg-iris-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
};
export function ProgressBar({ value = 0, tone = 'primary', showLabel = false }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-2.5">
      <div
        className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full ${PROGRESS_TONES[tone] || PROGRESS_TONES.primary}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showLabel && <span className="text-xs font-semibold text-slate-600 tabular-nums">{pct}%</span>}
    </div>
  );
}

// ─── KpiCard (bandeau de KPI) ───
const KPI_TONES = {
  default: { ring: 'border-slate-200', icon: 'bg-slate-100 text-slate-500' },
  primary: { ring: 'border-iris-100', icon: 'bg-iris-100 text-iris-600' },
  success: { ring: 'border-emerald-100', icon: 'bg-emerald-100 text-emerald-600' },
  warning: { ring: 'border-amber-100', icon: 'bg-amber-100 text-amber-700' },
};
export function KpiCard({ label, value, unit, delta, deltaDirection = 'up', tone = 'default', icon, footer }) {
  const t = KPI_TONES[tone] || KPI_TONES.default;
  const deltaUp = deltaDirection !== 'down';
  return (
    <div className={`bg-white rounded-xl border ${t.ring} shadow-xs p-4 flex flex-col gap-2.5`}>
      <div className="flex items-center justify-between">
        <span className="text-2xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
        {icon && (
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-none ${t.icon}`} aria-hidden="true">
            {icon}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display text-2xl font-bold text-slate-900 tracking-tight">{value}</span>
        {unit && <span className="text-sm font-semibold text-slate-500">{unit}</span>}
        {delta && (
          <span className={`ml-auto text-xs font-bold ${deltaUp ? 'text-emerald-600' : 'text-rose-600'}`}>
            {deltaUp ? '▲' : '▼'} {delta}
          </span>
        )}
      </div>
      {footer && <span className="text-2xs text-slate-400">{footer}</span>}
    </div>
  );
}

// ─── ActivityTimeline (appels / emails / notes) ───
const ACTIVITY_META = {
  call: { icon: Phone, wrap: 'bg-emerald-50 text-emerald-600', label: 'Appel' },
  email: { icon: Mail, wrap: 'bg-sky-50 text-sky-600', label: 'Email' },
  note: { icon: StickyNote, wrap: 'bg-amber-50 text-amber-700', label: 'Note' },
};
export function ActivityTimeline({ items = [] }) {
  if (items.length === 0) {
    return <p className="text-sm text-slate-400">Aucune activité enregistrée.</p>;
  }
  return (
    <ol className="relative list-none m-0 p-0">
      {items.map((a, i) => {
        const m = ACTIVITY_META[a.type] || ACTIVITY_META.note;
        const Icon = m.icon;
        const last = i === items.length - 1;
        return (
          <li key={i} className={`flex gap-3.5 relative ${last ? '' : 'pb-5'}`}>
            {!last && <span className="absolute left-[17px] top-9 bottom-0 w-0.5 bg-slate-200" aria-hidden="true" />}
            <span
              className={`w-9 h-9 rounded-full flex-none flex items-center justify-center z-[1] border-2 border-white ${m.wrap}`}
              aria-hidden="true"
            >
              <Icon size={17} />
            </span>
            <div className="flex-1 pt-0.5">
              <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                <span className="text-sm font-semibold text-slate-900">{m.label}</span>
                <span className="text-2xs text-slate-400">· {a.who} · {a.time}</span>
              </div>
              <p className="m-0 text-sm text-slate-700 leading-normal">{a.text}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ─── PropRow (rail de propriétés) ───
export function PropRow({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-2xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  );
}

// ─── EmptyState (entité introuvable) ───
export function EmptyState({ title, message, children }) {
  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center">
      <h1 className="font-display text-xl font-bold text-slate-900 mb-2">{title}</h1>
      <p className="text-sm text-slate-500 mb-6">{message}</p>
      {children}
    </div>
  );
}
